import { Injectable, OnDestroy } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription, filter, merge } from 'rxjs';

/**
 * Material paginator labels from the root translation file (`paginator.*`), following the
 * active language. Provided once for the whole app in `TranslocoRootModule`.
 */
@Injectable()
export class TranslocoPaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private readonly subscription: Subscription;

  constructor(private readonly translocoService: TranslocoService) {
    super();
    this.applyLabels();
    this.subscription = merge(
      this.translocoService.langChanges$,
      this.translocoService.events$.pipe(filter((event) => event.type === 'translationLoadSuccess'))
    ).subscribe(() => this.applyLabels());
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return this.translocoService.translate('paginator.rangeEmpty', { length });
    }
    const total = Math.max(length, 0);
    const start = page * pageSize;
    const end = start < total ? Math.min(start + pageSize, total) : start + pageSize;
    return this.translocoService.translate('paginator.range', { start: start + 1, end, length: total });
  };

  private applyLabels(): void {
    this.itemsPerPageLabel = this.translocoService.translate('paginator.itemsPerPage');
    this.nextPageLabel = this.translocoService.translate('paginator.nextPage');
    this.previousPageLabel = this.translocoService.translate('paginator.previousPage');
    this.firstPageLabel = this.translocoService.translate('paginator.firstPage');
    this.lastPageLabel = this.translocoService.translate('paginator.lastPage');
    // Tells every paginator on screen to re-read the labels.
    this.changes.next();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
